import React, { CSSProperties, useCallback, useEffect, useMemo, useRef } from 'react';
import { Button, Card, CircularProgress } from '@material-ui/core';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useIntl } from 'react-intl';
import Preloader from '../../../../../components/other/Preloader/Preloader';
import { useStyle } from '../styles';
import { formatDateFormat } from '../../../../../utils/utils';
import { TChannel } from '../../../../../interfaces/chat';
import { IUser } from '../../../../../interfaces/user';
import { useDefineUserRole, useFormatMessage } from '../../../../../hooks';
import { TMessageGPT } from '../hooks/useGetMessagesGPT';

const parseText = (input: string) => {
  const cleanedInput = input.replace(/###/g, '');
  const parts = cleanedInput.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

interface IProps {
  currentChat: TChannel | null;
  loadingMessages: boolean;
  profileMe: IUser;
  state?: any;
  messagesGPT: TMessageGPT[];
  isGPT: boolean;
  sendMessage?: (message: string) => void;
  isFull?: boolean;
  showUser?: string;
  hideName?: boolean;
  btnDelete?: () => void;
  title?: string;
  cardStyles?: CSSProperties;
  loadingSendGPT?: boolean;
}

const ListMessages: React.FC<IProps> = ({
  state,
  currentChat,
  loadingMessages,
  profileMe,
  messagesGPT,
  isGPT,
  isFull,
  showUser,
  btnDelete,
  hideName,
  title,
  cardStyles,
  loadingSendGPT,
}) => {
  const classes = useStyle();
  const fm = useFormatMessage();
  const { locale } = useIntl();
  const scrollBarref = useRef<HTMLDivElement>(null);
  const isAdmin = useDefineUserRole(profileMe, 'ROLE_ADMIN');
  const isManager = useDefineUserRole(profileMe, 'ROLE_MANAGER');
  const isBuyer = useDefineUserRole(profileMe, ['ROLE_BUYER']);
  const isVendor = useDefineUserRole(profileMe, 'ROLE_VENDOR');

  const nameChatPhone = useMemo(() => {
    if (currentChat && (isAdmin || isManager)) {
      const { company, user } = currentChat;
      if (company) return `Продавец ${company.name || ''}`;
      if (user && user?.roles?.includes('ROLE_BUYER') && user.phone_numbers !== null)
        return `Покупатель ${user.phone}`;
      if (user && user?.roles?.includes('ROLE_VENDOR') && user.phone_numbers !== null)
        return `Продавец ${user.phone_numbers || ''}`;
    }
  }, [currentChat, isAdmin, isManager]);

  const getNameChat = useCallback(
    (item: TChannel) => {
      if (isBuyer) return fm('SUPPORT');
      if (isVendor) {
        if (!item.company_id) return fm('SUPPORT');
        if (item.company_id) return fm('COMPANY.SUPPORT');
      }
      if (item?.user?.roles?.includes('ROLE_BUYER')) {
        return `${fm('USER.ROLES.BUYER')} ${item?.user?.phone || item?.user?.email || ''}`;
      }
      if (item?.user?.roles?.includes('ROLE_VENDOR')) {
        return `${fm('USER.ROLES.VENDOR')} ${item?.user?.email}`;
      }
      if (item?.user?.roles?.includes('ROLE_VENDOR') && state === undefined) {
        return `${fm('USER.ROLES.VENDOR')} ${item?.user?.email}`;
      }
      if (isAdmin || isManager) {
        return item.display_name;
      }
      return fm('SUPPORT');
    },
    [isAdmin, isManager, nameChatPhone, isBuyer, isVendor]
  );

  const scrollToBottom = useCallback(() => {
    scrollBarref.current && scrollBarref.current.scrollIntoView();
  }, [scrollBarref]);

  useEffect(() => {
    scrollToBottom();
  }, [loadingMessages, scrollBarref]);

  useEffect(() => {
    if (isGPT) {
      scrollToBottom();
    }
  }, [isGPT, messagesGPT.length]);

  return (
    <Card
      className={classes.chatCard}
      style={{
        ...cardStyles,
        width: isFull ? '100%' : undefined,
      }}
    >
      <>
        {(isGPT || currentChat) && (
          <div className={classes.chatHeader}>
            <b className={classes.chatName}>{`${
              isGPT ? title || 'ChatGPT' : getNameChat(currentChat!)
            }`}</b>
          </div>
        )}
        <PerfectScrollbar className={classes.scrollBarChat}>
          <>
            <>
              {loadingMessages ? (
                <Preloader />
              ) : (
                <>
                  {messagesGPT.map((item, index) => (
                    <div key={item.id}>
                      {item.role === 'user' && index === 0 && (
                        <div className={classes.myMessage}>
                          <div className={classes.myMessageUser}>
                            <b className={classes.myMessageTime}>
                              {formatDateFormat(
                                item.date,
                                'DD.MM.YYYY HH:mm:ss',
                                true,
                                locale
                              )}
                            </b>
                            {!hideName && (
                              <b className={classes.myMessageUserName}>{showUser || 'Я'}</b>
                            )}
                          </div>
                          <div className={classes.myMessageTextWrapper}>
                            <div
                              className={classes.myMessageTextBlock}
                              style={{
                                padding: item.content.trim() ? '1.25rem' : 0,
                              }}
                            >
                              <b className={classes.myMessageText}>
                                {parseText(item.content.trim())}
                              </b>
                            </div>
                          </div>
                        </div>
                      )}
                      {item.role === 'assistant' && index === 0 && (
                        <div className={classes.message}>
                          <div className={classes.messageUser}>
                            {/* <div className={classes.chatAvatar}>
                                <b style={{ color: '#009ef7' }}>C</b>
                              </div> */}
                            {!hideName && <b className={classes.messageUserName}>ChatGPT</b>}
                            <b className={classes.messageUserTime}>
                              {formatDateFormat(
                                item.date,
                                'DD.MM.YYYY HH:mm:ss',
                                true,
                                locale
                              )}
                            </b>
                          </div>
                          <div className={classes.messageTextWrapper}>
                            <b className={classes.messageText}>
                              {parseText(item.content.trim())}
                            </b>
                          </div>
                        </div>
                      )}
                      {item.role === 'user' &&
                        index > 0 &&
                        messagesGPT[index - 1].role === 'assistant' && (
                          <div className={classes.myMessage}>
                            <div className={classes.myMessageUser}>
                              <b className={classes.myMessageTime}>
                                {formatDateFormat(
                                  item.date,
                                  'DD.MM.YYYY HH:mm:ss',
                                  true,
                                  locale
                                )}
                              </b>
                              {!hideName && (
                                <b className={classes.myMessageUserName}>{showUser || 'Я'}</b>
                              )}
                            </div>
                            <div className={classes.myMessageTextWrapper}>
                              <div
                                className={classes.myMessageTextBlock}
                                style={{
                                  padding: item.content.trim() ? '1.25rem' : 0,
                                }}
                              >
                                <b className={classes.myMessageText}>
                                  {parseText(item.content.trim())}
                                </b>
                              </div>
                            </div>
                          </div>
                        )}
                      {item.role === 'user' &&
                        index > 0 &&
                        messagesGPT[index - 1].role === 'user' && (
                          <div className={classes.myMessage}>
                            <div className={classes.myMessageUser}>
                              <b className={classes.myMessageTime}>
                                {formatDateFormat(
                                  item.date,
                                  'DD.MM.YYYY HH:mm:ss',
                                  true,
                                  locale
                                )}
                              </b>
                            </div>
                            <div className={classes.myMessageTextWrapper}>
                              <div
                                className={classes.myMessageTextBlock}
                                style={{
                                  padding: item.content.trim() ? '1.25rem' : 0,
                                }}
                              >
                                <b className={classes.myMessageText}>
                                  {parseText(item.content.trim())}
                                </b>
                              </div>
                            </div>
                          </div>
                        )}
                      {item.role === 'assistant' &&
                        index > 0 &&
                        messagesGPT[index - 1].role === 'user' && (
                          <div className={classes.message}>
                            <div className={classes.messageUser}>
                              {/* <div className={classes.chatAvatar}>
                                  <b style={{ color: '#009ef7' }}>С</b>
                                </div> */}
                              {!hideName && <b className={classes.messageUserName}>ChatGPT</b>}
                              <b className={classes.messageUserTime}>
                                {formatDateFormat(
                                  item.date,
                                  'DD.MM.YYYY HH:mm:ss',
                                  true,
                                  locale
                                )}
                              </b>
                            </div>
                            <div className={classes.messageTextWrapper}>
                              <b className={classes.messageText}>
                                {parseText(item.content.trim())}
                              </b>
                            </div>
                          </div>
                        )}
                      {item.role === 'assistant' &&
                        index > 0 &&
                        messagesGPT[index - 1].role === 'assistant' && (
                          <div className={classes.message}>
                            <div className={classes.messageUser}>
                              <b className={classes.messageUserTime}>
                                {formatDateFormat(
                                  item.date,
                                  'DD.MM.YYYY HH:mm:ss',
                                  true,
                                  locale
                                )}
                              </b>
                            </div>
                            <div className={classes.messageTextWrapper}>
                              <b className={classes.messageText}>
                                {parseText(item.content.trim())}
                              </b>
                            </div>
                          </div>
                        )}
                    </div>
                  ))}
                  {loadingSendGPT && (
                    <div className={classes.gptLoader}>
                      <CircularProgress />
                    </div>
                  )}
                </>
              )}
            </>
            <div ref={scrollBarref} />
          </>
        </PerfectScrollbar>
        {btnDelete && (
          <Button
            onClick={btnDelete}
            variant='outlined'
            color='secondary'
            className={classes.btnDelete}
          >
            {fm('COMMON.BUTTON.DELETE')}
          </Button>
        )}
      </>
    </Card>
  );
};

export default React.memo(ListMessages);
